import { AppIcon, type IconName } from '@/components/ui/icons';
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspaceAuthority } from '../contexts/WorkspaceAuthorityContext';
import {
  getAccountCapabilities,
  isPlatformAccountRole,
  canAccessUsersRoute,
  canViewBusinessAnalytics,
  canViewProjectAnalytics,
  canViewSystemAnalytics,
  canViewSecurity,
  canViewDebug,
  canViewPlatformCustomers,
  canViewPlatformCommercial,
  canViewPlatformOperations,
  canViewPlatformAccess,
  canViewOrganisationCommercial,
} from '../lib/auth/capabilities';
import { useSecurityScopeAuthority } from '../hooks/useSecurityScopeAuthority';
import { resolveIdentityRoles } from '../lib/auth/identityRoles';
import {
  canEnterWorkspaceSurface,
  isFocusedAssignmentParticipant,
} from '../lib/auth/workspaceSurfaceEntry';
import { isAwaitingAssignment } from '../lib/auth/awaitingAssignment';
import Tooltip from './Tooltip';
import UserBadge from './UserBadge';
import { useBrandTheme } from '../contexts/BrandThemeContext';
import {
  SHOW_AI_QUALITY,
} from '../lib/featureFlags';
import { ENABLE_INTERNAL_ROUTES } from '../config/env';
import { getUserRouteAccess } from '../lib/auth/routeRegistry';
import { customerRouteHref } from '@/lib/navigation/canonicalRoutes';
import { resolveRelationshipPortalNavHref } from '@/lib/auth/dashboardPartition';
import { resolveWorkspaceAuthority } from '@/lib/auth/workspaceAuthority';
import { lookupRuntimeWorkspace } from '@/lib/auth/runtimeWorkspaceLookup';
import { resolveSidebarIdentity } from '@/lib/layout/resolvePortalSidebarIdentity';
import { useStakeholderParticipationContext } from '@/hooks/useStakeholderParticipationContext';
import PortalSidebarIdentityCard from '@/components/Layout/PortalSidebarIdentityCard';
import { surfaceFromRequestedPath } from '@/lib/navigation/returnTarget';
import { resolveActiveNavigationHref, type NavigationMatchStrategy } from '@/lib/navigation/navigationModel';
import {
  SHELL_NAV,
  shellNavScrollerClass,
} from '@/lib/layout/shellNav';
import NavigationContextMenu from '@/components/NavigationContextMenu';
import { useActiveNavigationContext } from '@/hooks/useActiveNavigationContext';
import ContextualSidebarNavItem, {
  type ContextualSidebarNavChild,
} from '@/components/Layout/ContextualSidebarNavItem';
import {
  PROJECT_DETAIL_PRIMARY_TAB_DEFINITIONS,
  resolveProjectDetailPrimaryTab,
  type ProjectTabParam,
} from '@/utils/projectDetailNavigation';
import {
  isAuthorityProjectionFresh,
  useAuthorityProjectionState,
} from '@/lib/auth/authorityProjectionStore';
import { authorityProjectionMatchesNavigationPath } from '@/services/AuthorityProjectionService';
import { Avatar } from '@/features/ui/components/Avatar';

// ============================================================================
// MOTION TOKENS - Enterprise 2026
// Calm, precise, functional motion. No decoration.
// ============================================================================


const motion_tokens = {
  duration: {
    fast: 0.12,      // 120ms - hover states, micro-interactions
    normal: 0.16,    // 160ms - standard transitions
    slow: 0.2,       // 200ms - layout changes, panel slides
  },
  easing: {
    // Professional ease-out: quick start, smooth stop
    out: [0.2, 0, 0, 1] as const,
    // Smooth in-out for bidirectional transitions
    inOut: [0.4, 0, 0.2, 1] as const,
    // Linear for opacity-only changes
    linear: [0, 0, 1, 1] as const,
  },
};

// Framer motion transition presets
const transitions = {
  sidebar: {
    duration: motion_tokens.duration.slow,
    ease: motion_tokens.easing.out,
  },
  item: {
    duration: motion_tokens.duration.fast,
    ease: motion_tokens.easing.out,
  },
  indicator: {
    duration: motion_tokens.duration.normal,
    ease: motion_tokens.easing.inOut,
  },
  label: {
    duration: motion_tokens.duration.fast,
    ease: motion_tokens.easing.out,
  },
};

// ============================================================================
// TYPES
// ============================================================================

interface SidebarNavigationProps {
  className?: string;
  isCollapsed?: boolean;
}

interface NavItem {
  to: string;
  icon: IconName;
  label: string;
  description: string;
  activePaths?: string[];
  match?: NavigationMatchStrategy;
  children?: ContextualSidebarNavChild[];
}

interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

// ============================================================================
// SECTION LABEL COMPONENT
// ============================================================================

const SectionLabel: React.FC<{ label: string; isCollapsed: boolean }> = ({ label, isCollapsed }) => {
  if (isCollapsed) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={transitions.label}
        className="h-px bg-[var(--sidebar-section-border)] mx-2 my-2"
      />
    );
  }
  return (
    <div className="px-3 pt-4 pb-1.5 first:pt-0">
      <motion.span
        initial={{ opacity: 0, x: -2 }}
        animate={{ opacity: 1, x: 0 }}
        transition={transitions.label}
        className={cn(SHELL_NAV.sectionLabel, 'block')}
      >
        {label}
      </motion.span>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  className,
  isCollapsed = false,
}) => {
  const { user, isDemoMode } = useAuth();
  const location = useLocation();
  const { isWorkspaceBrandingActive } = useBrandTheme();
  const { workspaceId: validatedWorkspaceId, status: workspaceStatus } = useWorkspaceAuthority();
  const authorityProjection = useAuthorityProjectionState();
  const connected = isDemoMode === false;
  const connectedProjection = React.useMemo(() => {
    if (!connected
      || authorityProjection.status !== 'confirmed'
      || authorityProjection.projection.actor.userId !== user?.id
      || !isAuthorityProjectionFresh(
        authorityProjection.projection,
        authorityProjection.validatedAt,
      )
      || !authorityProjectionMatchesNavigationPath(
        authorityProjection.request,
        authorityProjection.projection,
        location.pathname,
      )) {
      return null;
    }
    return authorityProjection.projection;
  }, [authorityProjection, connected, location.pathname, user?.id]);

  // Single canonical role source: account and platform planes stay independent.
  const { accountRole, platformRole } = resolveIdentityRoles(user);
  // The sidebar is rendered by the application shell outside the nested route
  // boundary. During a client-side transition that boundary can still be
  // resolving even though the URL already carries the canonical workspace.
  // Preserve that *route* scope for navigation so a click never falls back to
  // the asynchronous legacy redirect (which was the source of pages only
  // opening after a refresh). This is a URL hint, not an authority decision:
  // the WorkspaceAuthorityBoundary remains the guard for the destination.
  const routeWorkspaceId = React.useMemo(() => {
    const match = /^\/workspaces\/([^/]+)(?:\/|$)/.exec(location.pathname);
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return null;
    }
  }, [location.pathname]);
  const routeWorkspaceAuthority = React.useMemo(() => (
    routeWorkspaceId && !connected
      ? resolveWorkspaceAuthority({
          routeWorkspaceId,
          user,
          lookupWorkspace: (workspaceId) => lookupRuntimeWorkspace(workspaceId, user),
        })
      : null
  ), [connected, routeWorkspaceId, user]);
  const scopedWorkspaceId = connected
    ? connectedProjection?.scope.workspaceId ?? null
    : workspaceStatus === 'ready'
      ? validatedWorkspaceId
      : routeWorkspaceId;
  // Navigation may preserve the canonical URL scope while the nested route
  // boundary resolves. Security capability reads must never use that hint as
  // authority; they require the validated workspace result.
  const securityWorkspaceId = connected
    ? connectedProjection?.scope.workspaceId ?? null
    : workspaceStatus === 'ready'
      ? validatedWorkspaceId
      : routeWorkspaceAuthority?.status === 'ready'
        ? routeWorkspaceAuthority.workspaceId
        : null;
  const { contexts, activeContext, selectContext } = useActiveNavigationContext(
    connected ? null : user,
    connected ? null : scopedWorkspaceId,
  );
  const [contextMenuOpen, setContextMenuOpen] = React.useState(false);

  // Account-plane capabilities drive global navigation. Scoped relationships
  // additionally grant workspace surface entry (see canEnterWorkspaceSurface).
  const caps = React.useMemo(
    () => getAccountCapabilities(user),
    [user],
  );
  const hasCanonicalPlatformAccount = isPlatformAccountRole(user?.account_role);
  const securityAuthorityEnabled = !connected
    && (hasCanonicalPlatformAccount || securityWorkspaceId !== null);
  const securityAuthority = useSecurityScopeAuthority({
    scopeWorkspaceId: hasCanonicalPlatformAccount ? null : securityWorkspaceId,
    scopeKey: hasCanonicalPlatformAccount
      ? `sidebar:platform:${user?.id ?? 'anonymous'}`
      : `sidebar:workspace:${securityWorkspaceId ?? 'none'}:${user?.id ?? 'anonymous'}`,
    user,
    demoAal: 'aal1',
    enabled: securityAuthorityEnabled,
  });
  const securityCaps = securityAuthority.grantedCapabilities;

  const stakeholderContext = useStakeholderParticipationContext(
    connected ? null : user,
    connected ? null : location.pathname,
  );

  // Relationship portals, project assignment shells, and organisation Owner/Admin/Member
  // share Investor-style top identity chrome. Project route (when present) selects
  // which assigned project appears on the Project Management card, and drives
  // company↔project participation labels for stakeholder portals.
  // When the route has no `/projects/:id`, fall back to project-scoped stakeholder
  // representation (or a single company↔project link) so dashboard/home still
  // reflects Investee / Partner / etc. instead of generic Company Portal.
  const sidebarIdentity = React.useMemo(() => {
    if (connected) return null;
    return resolveSidebarIdentity(user, {
      projectId: stakeholderContext.projectId,
      projectName: stakeholderContext.projectName,
      companyParticipation: stakeholderContext.companyParticipation,
      navigationContextKind: activeContext?.kind === 'investor' ? null : activeContext?.kind,
      navigationContextResourceId: activeContext?.resourceId,
    });
  }, [
    user,
    stakeholderContext.projectId,
    stakeholderContext.projectName,
    stakeholderContext.companyParticipation,
    activeContext?.kind,
    activeContext?.resourceId,
    connected,
  ]);

  const workspaceSwitcherHref = React.useMemo(() => {
    const params = new URLSearchParams();
    if (scopedWorkspaceId) params.set('requestedWorkspaceId', scopedWorkspaceId);
    const returnSurface = surfaceFromRequestedPath(location.pathname);
    if (returnSurface) params.set('returnTo', returnSurface);
    if (returnSurface === 'settings') {
      const settingsTab = new URLSearchParams(location.search).get('tab');
      if (settingsTab) params.set('settingsTab', settingsTab);
    }
    const query = params.toString();
    return query ? `/workspaces/select?${query}` : '/workspaces/select';
  }, [location.pathname, location.search, scopedWorkspaceId]);

  const accountSettingsHref = connected
    ? connectedProjection?.scope.workspaceId
      && new Set<string>(connectedProjection.capabilities).has('workspace.read')
      ? `${customerRouteHref(connectedProjection.scope.workspaceId, 'settings')}?tab=account`
      : '/unauthorized'
    : scopedWorkspaceId
      ? `${customerRouteHref(scopedWorkspaceId, 'settings')}?tab=account`
      : '/settings?tab=account';

  const contextualProjectNavigation = React.useMemo(() => {
    const workspaceProjectMatch = location.pathname.match(
      /^\/workspaces\/[^/]+\/projects\/[^/]+\/?$/,
    );
    const companyProjectMatch = location.pathname.match(
      /^\/companies\/([^/]+)\/projects\/([^/]+)\/?$/,
    );
    if (!workspaceProjectMatch && !companyProjectMatch) return null;

    const params = new URLSearchParams(location.search);
    const currentTab = params.get('tab');
    const makeHref = (tab: string) => {
      const next = new URLSearchParams(location.search);
      next.set('tab', tab);
      next.delete('decision');
      next.delete('history');
      next.delete('intent');
      return `${location.pathname}?${next.toString()}`;
    };

    if (companyProjectMatch) {
      const activeTab = ['overview', 'milestones', 'documents', 'reports'].includes(currentTab ?? '')
        ? currentTab
        : 'overview';
      const [companyId, projectId] = [companyProjectMatch[1], companyProjectMatch[2]];
      return [
        { id: 'overview', label: 'Overview', icon: 'layoutDashboard' as const },
        { id: 'milestones', label: 'Milestones', icon: 'milestone' as const },
        { id: 'documents', label: 'Documents', icon: 'fileText' as const },
        { id: 'reports', label: 'Reports', icon: 'report' as const },
      ].map((item) => ({
        to: item.id === 'reports'
          ? `/companies/${encodeURIComponent(companyId)}/reports?tab=projectDetails&projectId=${encodeURIComponent(projectId)}`
          : makeHref(item.id),
        label: item.label,
        icon: item.icon,
        isActive: activeTab === item.id,
      }));
    }

    const activeTab = resolveProjectDetailPrimaryTab(currentTab as ProjectTabParam | null);
    return PROJECT_DETAIL_PRIMARY_TAB_DEFINITIONS.map((item) => ({
      to: makeHref(item.id),
      label: item.label,
      icon: item.icon,
      isActive: activeTab === item.id,
    }));
  }, [location.pathname, location.search]);

  const analyticsNavigation = React.useMemo<ContextualSidebarNavChild[] | null>(() => {
    if (location.pathname === '/analytics/project-manager') {
      const activeSection = new URLSearchParams(location.search).get('section') ?? 'milestones';
      return [
        { id: 'milestones', label: 'Milestones', icon: 'milestone' as const },
        { id: 'burndown', label: 'Burn-down / Burn-up', icon: 'trendingUp' as const },
        { id: 'flow', label: 'Task flow', icon: 'activity' as const },
        { id: 'risks', label: 'Risks & blockers', icon: 'alertTriangle' as const },
      ].map((item) => ({
        to: `/analytics/project-manager?section=${item.id}`,
        label: item.label,
        icon: item.icon,
        isActive: activeSection === item.id,
      }));
    }
    if (location.pathname !== '/analytics') return null;
    const requestedTab = new URLSearchParams(location.search).get('tab');
    const definitions: Array<{ id: string; label: string; icon: IconName; visible: boolean }> = [
      { id: 'overview', label: 'Overview', icon: 'layoutDashboard', visible: canViewBusinessAnalytics(caps) },
      { id: 'projects', label: 'Projects', icon: 'project', visible: canViewProjectAnalytics(caps) },
      { id: 'users', label: 'Users', icon: 'users', visible: caps.includes('analytics.view.users') || caps.includes('users.view.global') },
      { id: 'system', label: 'System', icon: 'server', visible: canViewSystemAnalytics(caps) },
    ];
    const visibleDefinitions = definitions.filter((item) => item.visible);
    const activeTab = requestedTab && visibleDefinitions.some((item) => item.id === requestedTab)
      ? requestedTab
      : visibleDefinitions[0]?.id;
    return visibleDefinitions.map((item) => ({
      to: `/analytics?tab=${item.id}`,
      label: item.label,
      icon: item.icon,
      isActive: activeTab === item.id,
    }));
  }, [caps, location.pathname, location.search]);

  // ============================================================================
  // NAVIGATION ITEMS LOGIC (preserved from original)
  // ============================================================================

  const getGroupedNavigation = (): NavSection[] => {
    if (connected) {
      if (!connectedProjection) return [];
      const projectedCapabilities = new Set<string>(connectedProjection.capabilities);
      const projectedWorkspaceId = connectedProjection.scope.workspaceId;
      const representedCompany = connectedProjection.scope.representedEntity;
      const projectedResource = connectedProjection.scope.resource;

      if (
        representedCompany?.type === 'company'
        && projectedResource?.type === 'company'
        && representedCompany.id === projectedResource.id
        && projectedCapabilities.has('company.read')
      ) {
        const companyId = representedCompany.id;
        return [{
          id: 'company-portal',
          label: 'Company Portal',
          items: [
            { to: `/companies/${encodeURIComponent(companyId)}/dashboard`, icon: 'layoutDashboard', label: 'Dashboard', description: 'Company overview' },
            { to: `/companies/${encodeURIComponent(companyId)}/projects`, icon: 'project', label: 'Projects', description: 'Authorized company projects' },
            { to: `/companies/${encodeURIComponent(companyId)}/tasks`, icon: 'task', label: 'Tasks', description: 'Authorized company tasks' },
            { to: `/companies/${encodeURIComponent(companyId)}/assets`, icon: 'dataRoom', label: 'Assets', description: 'Authorized company documents' },
            { to: `/companies/${encodeURIComponent(companyId)}/reports`, icon: 'report', label: 'Reports', description: 'Authorized company reports' },
            { to: `/companies/${encodeURIComponent(companyId)}/decisions`, icon: 'decision', label: 'Decisions', description: 'Authorized company decisions' },
          ],
        }];
      }

      if (!projectedWorkspaceId) return [];
      const workItems: NavItem[] = [];
      const operationsItems: NavItem[] = [];
      const accountItems: NavItem[] = [];

      if (projectedCapabilities.has('workspace.read')) {
        workItems.push(
          { to: customerRouteHref(projectedWorkspaceId, 'dashboard'), icon: 'layoutDashboard', label: 'Dashboard', description: 'Workspace overview' },
          { to: customerRouteHref(projectedWorkspaceId, 'projects'), icon: 'project', label: 'Projects', description: 'Server-filtered project portfolio' },
          { to: customerRouteHref(projectedWorkspaceId, 'tasks'), icon: 'task', label: 'Tasks', description: 'Server-filtered task list' },
        );
        operationsItems.push(
          { to: customerRouteHref(projectedWorkspaceId, 'assets'), icon: 'dataRoom', label: 'Assets', description: 'Server-filtered documents and data rooms' },
          { to: customerRouteHref(projectedWorkspaceId, 'decisions'), icon: 'decision', label: 'Decisions', description: 'Server-filtered governance queue' },
        );
        accountItems.push({
          to: customerRouteHref(projectedWorkspaceId, 'settings'),
          icon: 'settings',
          label: 'Settings',
          description: 'Account preferences',
        });
      } else if (projectedCapabilities.has('project.read') && projectedResource?.type === 'project') {
        workItems.push({
          to: `/workspaces/${encodeURIComponent(projectedWorkspaceId)}/projects/${encodeURIComponent(projectedResource.id)}`,
          icon: 'project',
          label: 'Project',
          description: 'Authorized project',
          activePaths: contextualProjectNavigation ? [location.pathname] : undefined,
          children: contextualProjectNavigation ?? undefined,
        });
      }

      if (projectedCapabilities.has('organization.users.read')) {
        operationsItems.push({
          to: customerRouteHref(projectedWorkspaceId, 'users'),
          icon: 'team',
          label: 'Team',
          description: 'Authorized collaborator directory',
        });
      }

      return [
        ...(workItems.length ? [{ id: 'work', label: 'Work', items: workItems }] : []),
        ...(operationsItems.length ? [{ id: 'operations', label: 'Operations', items: operationsItems }] : []),
        ...(accountItems.length ? [{ id: 'account', label: 'Account', items: accountItems }] : []),
      ];
    }

    // Unassigned Workspace Members: Welcome + Notifications + Settings only.
    if (isAwaitingAssignment(user)) {
      const welcomeHref = scopedWorkspaceId
        ? customerRouteHref(scopedWorkspaceId, 'dashboard')
        : '/dashboard';
      const settingsHref = scopedWorkspaceId
        ? customerRouteHref(scopedWorkspaceId, 'settings')
        : '/settings';
      return [
        {
          id: 'home',
          label: 'Home',
          items: [
            {
              to: welcomeHref,
              icon: 'layoutDashboard',
              label: 'Welcome',
              description: 'Awaiting assignment',
            },
            {
              to: `${settingsHref}?tab=notifications`,
              icon: 'bell',
              label: 'Notifications',
              description: 'Your notification preferences and inbox',
            },
            {
              to: settingsHref,
              icon: 'settings',
              label: 'Settings',
              description: 'Account preferences',
            },
          ],
        },
      ];
    }

    // AX1 internal accounts use the Control Plane shell. It deliberately omits
    // Projects, Tasks, Assets, Calendar, ChatHub and Companies because a
    // platform role is not standing membership in a customer workspace.
    if (platformRole) {
      const controlPlaneItems: NavItem[] = [
        {
          to: '/platform',
          icon: 'layoutDashboard',
          label: 'Overview',
          description: 'Control Plane exceptions and posture',
          match: 'exact',
        },
      ];

      if (canViewPlatformCustomers(caps)) {
        controlPlaneItems.push({
          to: '/platform/customers',
          icon: 'company',
          label: 'Customers',
          description: 'Customer and tenant registry metadata',
        });
      }
      if (canViewPlatformCommercial(caps)) {
        controlPlaneItems.push({
          to: '/platform/commercial',
          icon: 'creditCard',
          label: 'Billing & commercial',
          description: 'Packages, contracts, billing and renewals',
        });
      }
      if (canViewPlatformOperations(caps)) {
        controlPlaneItems.push({
          to: '/platform/operations',
          icon: 'server',
          label: 'Operations',
          description: 'Environment, incident and backup posture',
        });
      }
      if (canViewPlatformAccess(caps)) {
        controlPlaneItems.push({
          to: '/platform/access',
          icon: 'key',
          label: 'Privileged Access',
          description: 'Eligible roles and time-bounded sessions',
        });
      }

      const administrationItems: NavItem[] = [];
      if (canAccessUsersRoute(caps)) {
        administrationItems.push({
          to: '/platform/team',
          icon: 'users',
          label: 'Platform Team',
          description: 'AX1 internal accounts and eligibility',
        });
      }
      if (canViewSecurity(securityCaps)) {
        administrationItems.push({
          to: '/platform/security',
          icon: 'security',
          label: 'Security',
          description: 'Platform security operations',
        });
      }
      if (
        caps.includes('platform.roles.assign')
        || caps.includes('platform.roles.activate')
        || caps.includes('platform.roles.activate.approve')
        || caps.includes('system.roles.manage')
      ) {
        administrationItems.push({
          to: '/settings/roles',
          icon: 'role',
          label: 'Roles',
          description: 'Separated platform and customer roles',
        });
      }
      administrationItems.push({
        to: '/settings',
        icon: 'settings',
        label: 'Settings',
        description: caps.includes('system.settings.edit')
          ? 'Platform and account configuration'
          : 'Account and security preferences',
      });
      if (ENABLE_INTERNAL_ROUTES && canViewDebug(caps)) {
        administrationItems.push({
          to: '/debug',
          icon: 'bug',
          label: 'Diagnostics',
          description: 'System diagnostics',
        });
      }

      return [
        { id: 'control-plane', label: 'Control Plane', items: controlPlaneItems },
        ...(administrationItems.length
          ? [{ id: 'administration', label: 'Administration', items: administrationItems }]
          : []),
      ];
    }

    // Build sections based on permissions
    const workSection: NavItem[] = [];
    const operationsSection: NavItem[] = [];
    const systemSection: NavItem[] = [];

    // Workspace-scoped href helper. Customer surfaces are entered through the
    // validated workspace route; when no home workspace is known we fall back
    // to explicit selection (never a flat surface that would bypass scoping).
    const wsHref = (
      subPath:
        | 'dashboard'
        | 'projects'
        | 'milestones'
        | 'calendar'
        | 'tasks'
        | 'assets'
        | 'companies'
        | 'reports'
        | 'chat'
        | 'investments'
        | 'decisions'
        | 'users'
        | 'security'
        | 'settings',
    ) => {
      if (scopedWorkspaceId) {
        return customerRouteHref(scopedWorkspaceId, subPath);
      }
      // Flat intent carriers resolve through LegacyWorkspaceSurfaceRedirect
      // (0 / 1 / many) — never auto-select accessibleWorkspaces[0].
      switch (subPath) {
        case 'dashboard':
          return '/dashboard';
        case 'projects':
          return '/projects';
        case 'milestones':
          return '/milestones';
        case 'calendar':
          return '/calendar';
        case 'tasks':
          return '/tasks';
        case 'assets':
          return '/assets';
        case 'companies':
          return '/companies';
        case 'reports':
          return '/reports';
        case 'chat':
          return '/chat';
        case 'investments':
          return '/investments';
        case 'decisions':
          return '/decisions';
        case 'users':
          return '/users';
        case 'security':
          return '/security';
        case 'settings':
          return '/settings';
      }
    };

    const contextAwareWsHref = (surface: Parameters<typeof wsHref>[0]) => {
      if (
        scopedWorkspaceId
        && (activeContext?.kind === 'partner' || activeContext?.kind === 'vendor')
      ) {
        return customerRouteHref(scopedWorkspaceId, surface, {
          search: { context: activeContext.key },
        });
      }
      return wsHref(surface);
    };

    // A reporting recipient, decision participant, validator or observer with
    // no operating relationship has an assignment inbox—not a generic
    // workspace. Show only destinations backed by the exact assigned resource,
    // plus personal Settings. This prevents attractive but empty/misleading
    // Dashboard, Calendar and Chat links.
    if (accountRole === 'organization_member' && isFocusedAssignmentParticipant(user)) {
      const assignedItems: NavItem[] = [];
      const focusedSurfaces: ReadonlyArray<{
        surface: 'reports' | 'decisions' | 'tasks' | 'milestones' | 'projects';
        icon: IconName;
        label: string;
        description: string;
      }> = [
        { surface: 'reports', icon: 'report', label: 'Reports', description: 'Reports assigned to you' },
        { surface: 'decisions', icon: 'decision', label: 'Decisions', description: 'Decisions assigned to you' },
        { surface: 'tasks', icon: 'task', label: 'Tasks', description: 'Tasks assigned to you' },
        { surface: 'milestones', icon: 'milestone', label: 'Milestones', description: 'Milestones assigned to you' },
        { surface: 'projects', icon: 'project', label: 'Projects', description: 'Projects assigned to you' },
      ];

      for (const item of focusedSurfaces) {
        if (!canEnterWorkspaceSurface(item.surface, user, caps)) continue;
        assignedItems.push({
          to: wsHref(item.surface),
          icon: item.icon,
          label: item.label,
          description: item.description,
        });
      }

      return [
        ...(assignedItems.length
          ? [{ id: 'assigned', label: 'Assigned to you', items: assignedItems }]
          : []),
        {
          id: 'account',
          label: 'Account',
          items: [{
            to: wsHref('settings'),
            icon: 'settings',
            label: 'Settings',
            description: 'Personal preferences',
          }],
        },
      ];
    }

    // === WORK SECTION ===

    // Relationship portals (Company / Partner / Vendor) must keep primary nav
    // inside `/companies|/partners|/vendors/:id/*` — never flat operating routes.
    const portalDashboardHref = resolveRelationshipPortalNavHref(user, 'dashboard', scopedWorkspaceId, activeContext);
    const portalProjectsHref = resolveRelationshipPortalNavHref(user, 'projects', scopedWorkspaceId, activeContext);
    const portalTasksHref = resolveRelationshipPortalNavHref(user, 'tasks', scopedWorkspaceId, activeContext);
    const portalAssetsHref = resolveRelationshipPortalNavHref(user, 'assets', scopedWorkspaceId, activeContext);
    const portalReportsHref = resolveRelationshipPortalNavHref(user, 'reports', scopedWorkspaceId, activeContext);
    const portalDecisionsHref = resolveRelationshipPortalNavHref(user, 'decisions', scopedWorkspaceId, activeContext);
    const isCompanyPortalNav = Boolean(portalDashboardHref?.startsWith('/companies/'));

    // Dashboard is the safe customer home for any resolved organization role.
    const showDashboard = Boolean(accountRole);

    if (showDashboard) {
      let description = 'Overview';
      if (portalDashboardHref) description = 'Portal overview';
      else if (canViewSystemAnalytics(caps) && !canViewBusinessAnalytics(caps)) description = 'System metrics';
      else if (canViewBusinessAnalytics(caps)) description = 'Business insights';
      else if (canViewProjectAnalytics(caps)) description = 'Project performance';

      workSection.push({
        to: portalDashboardHref ?? wsHref('dashboard'),
        icon: "layoutDashboard",
        label: 'Dashboard',
        description,
        activePaths: analyticsNavigation ? [location.pathname] : undefined,
        children: analyticsNavigation ?? undefined,
      });
    }

    // Projects — account capability OR a supporting scoped relationship.
    if (canEnterWorkspaceSurface('projects', user, caps)) {
      workSection.push({
        to: portalProjectsHref ?? wsHref('projects'),
        icon: "project",
        label: 'Projects',
        description: 'Manage project portfolio',
        activePaths: contextualProjectNavigation ? [location.pathname] : undefined,
        children: contextualProjectNavigation ?? undefined,
      });
    }

    // Milestones — account capability OR a supporting scoped relationship
    // (e.g. project_manager). Must not require org-wide projects.view alone.
    // Company representatives stay within their dedicated Company Portal,
    // which intentionally has no workspace-wide Milestones route. Partner,
    // Vendor and Project Manager relationships use the canonical workspace
    // Milestones surface, whose project filtering keeps the list scoped.
    if (canEnterWorkspaceSurface('milestones', user, caps) && !isCompanyPortalNav) {
      workSection.push({
        to: contextAwareWsHref('milestones'),
        icon: "milestone",
        label: 'Milestones',
        description: 'Milestones and Capital Gates',
        activePaths: ['/capital'],
      });
    }

    // Tasks — account capability OR a supporting scoped relationship.
    // Company Reps enter Tasks via Company Portal (relationship), not workspace tasks caps.
    if (canEnterWorkspaceSurface('tasks', user, caps) || portalTasksHref) {
      workSection.push({
        to: portalTasksHref ?? wsHref('tasks'),
        icon: "task",
        label: 'Tasks',
        description: 'Track action items'
      });
    }

    // Calendar - always visible for non-clients
    workSection.push({
      to: contextAwareWsHref('calendar'),
      icon: "calendar",
      label: 'Calendar',
      description: 'Schedule and deadlines'
    });

    // === OPERATIONS SECTION ===

    // Assets — account capability OR a supporting scoped relationship.
    if (canEnterWorkspaceSurface('assets', user, caps) || portalAssetsHref) {
      operationsSection.push({
        to: portalAssetsHref ?? wsHref('assets'),
        icon: "dataRoom",
        label: 'Assets',
        description: 'Documents and data rooms'
      });
    }

    // Users / Team — account capability OR workspace membership (collaborators).
    if (canEnterWorkspaceSurface('users', user, caps)) {
      operationsSection.push({
        to: wsHref('users'),
        icon: "team",
        label: caps.includes('users.view.global') ? 'Users' : 'Team',
        description: caps.includes('users.view.global') ? 'User management' : 'Collaborators'
      });
    }

    // Companies — account capability OR a supporting scoped relationship.
    // Suppressed inside the Company Portal: `Dashboard` already IS the portal
    // dashboard there, so a second entry pointing at the same href would be a
    // duplicate rather than a distinct surface.
    if (canEnterWorkspaceSurface('companies', user, caps) && !isCompanyPortalNav) {
      operationsSection.push({
        to: wsHref('companies'),
        icon: "company",
        label: 'Companies',
        description: 'Organizations'
      });
    }

    // Decisions — account capability (gates.view) OR decision/validator/observer scope.
    if (canEnterWorkspaceSurface('decisions', user, caps) || portalDecisionsHref) {
      operationsSection.push({
        to: portalDecisionsHref ?? wsHref('decisions'),
        icon: 'decision',
        label: 'Decisions',
        description: 'Governance and validation queue',
      });
    }

    // Reports — account capability OR an active reporting/investment scope.
    // Flat intent route for workspace resolution, unless a relationship portal owns reports.
    if (canEnterWorkspaceSurface('reports', user, caps) || portalReportsHref) {
      operationsSection.push({
        to: portalReportsHref ?? wsHref('reports'),
        icon: 'report',
        label: 'Reports',
        description: 'Delivered reports',
      });
    }

    // ChatHub
    operationsSection.push({
      to: contextAwareWsHref('chat'),
       
      icon: "chat",
      label: 'ChatHub',
      description: 'Team communication'
    });

    // === SYSTEM SECTION ===

    // AI Quality (admin only — internal AI metrics)
    if (
      ENABLE_INTERNAL_ROUTES
      && (accountRole === 'organization_admin' || accountRole === 'organization_owner')
      && SHOW_AI_QUALITY
    ) {
      systemSection.push({
        to: '/ai-quality',
        icon: "sparkles",
        label: 'AI Quality',
        description: 'AI output metrics'
      });
    }

    // Security — customer branch only reaches here (platform handled above).
    if (canViewOrganisationCommercial(caps)) {
      systemSection.push({
        to: `${wsHref('settings')}/billing`,
        icon: 'creditCard',
        label: 'Commercial account',
        description: caps.includes('commercial.pricing.view')
          ? 'Plan, contract, invoices and usage'
          : 'Plan, entitlements and usage',
      });
    }

    // Security — customer branch only reaches here (platform handled above).
    if (canViewSecurity(securityCaps)) {
      systemSection.push({
        to: wsHref('security'),
        icon: "security",
        label: 'Security',
        description: 'Workspace security'
      });
    }

    // Debug
    if (ENABLE_INTERNAL_ROUTES && canViewDebug(caps)) {
      systemSection.push({
        to: '/debug',
        icon: "bug",
        label: 'Debug',
        description: 'System diagnostics'
      });
    }

    // Settings is a personal-preferences entry for ordinary members. Admin
    // tabs remain independently capability-gated by the settings surface.
    systemSection.push({
      to: contextAwareWsHref('settings'),
      icon: "settings",
      label: 'Settings',
      description: caps.includes('system.settings.edit')
        ? 'Workspace and account configuration'
        : 'Personal preferences'
    });

    // Build sections array, only including non-empty sections
    const sections: NavSection[] = [];

    if (workSection.length > 0) {
      sections.push({ id: 'work', label: 'Work', items: workSection });
    }
    if (operationsSection.length > 0) {
      sections.push({ id: 'operations', label: 'Operations', items: operationsSection });
    }
    if (systemSection.length > 0) {
      sections.push({ id: 'system', label: 'System', items: systemSection });
    }

    return sections;
  };

  // Two items resolving to the same href are the same destination, so only the
  // first (highest in the nav order) survives. This is what keeps Company /
  // Partner / Vendor portal personas from seeing a portal entry alongside the
  // portal-aware `Dashboard` item.
  const navSections = (() => {
    if (!user) return [] as NavSection[];
    const seenHrefs = new Set<string>();
    return getGroupedNavigation()
      .map(section => ({
        ...section,
        items: section.items.filter(item => {
          if (!connected && !getUserRouteAccess(user, item.to).allowed) return false;
          if (seenHrefs.has(item.to)) return false;
          seenHrefs.add(item.to);
          return true;
        }),
      }))
      .filter(section => section.items.length > 0);
  })();

  const activeNavigationHref = resolveActiveNavigationHref(
    navSections.flatMap((section) => section.items),
    location.pathname,
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <motion.div
      initial={false}
      className={cn(
        'ax-nav-shell h-full w-full flex flex-col overflow-hidden',
        className
      )}
    >
      {/* Portal / organisation identity — same chrome as Investor Portal */}
      {sidebarIdentity && (
        <div className="relative">
          <PortalSidebarIdentityCard
            portalLabel={sidebarIdentity.portalLabel}
            displayName={sidebarIdentity.displayName}
            organizationName={sidebarIdentity.organizationName}
            typeLabel={sidebarIdentity.typeLabel}
            workspaceLabel={sidebarIdentity.workspaceLabel}
            status={sidebarIdentity.status}
            icon={sidebarIdentity.icon}
            presentation={sidebarIdentity.presentation}
            isCollapsed={isCollapsed}
            switcherHref={contexts.length > 1 || platformRole ? undefined : workspaceSwitcherHref}
            onSwitcherClick={contexts.length > 1 ? () => setContextMenuOpen((open) => !open) : undefined}
            transition={transitions.label}
          />
          {contextMenuOpen && contexts.length > 1 && (
            <NavigationContextMenu
              contexts={contexts}
              activeContext={activeContext}
              onSelect={selectContext}
              onClose={() => setContextMenuOpen(false)}
              className={isCollapsed ? 'left-full right-auto top-2 ml-2 w-72' : undefined}
            />
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className={shellNavScrollerClass(isCollapsed, sidebarIdentity ? undefined : 'py-3')}>
        {navSections.map((section, sectionIndex) => (
          <div key={section.id}>
            {/* Section Labels */}
            {sectionIndex > 0 && <SectionLabel label={section.label} isCollapsed={isCollapsed} />}
            {sectionIndex === 0 && (
              <AnimatePresence mode="wait">
                {!isCollapsed ? (
                  <motion.div
                    key="label"
                    initial={{ opacity: 0, x: -2 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -2 }}
                    transition={transitions.label}
                    className={cn('px-3 pb-1.5', sidebarIdentity && 'pt-3')}
                  >
                    <span className={SHELL_NAV.sectionLabel}>
                      {section.label}
                    </span>
                  </motion.div>
                ) : (
                  <motion.div
                    key="spacer"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={transitions.label}
                    className="h-1"
                  />
                )}
              </AnimatePresence>
            )}

            {/* Nav Items */}
            <div className={cn('space-y-0.5', isCollapsed ? 'space-y-1' : '')}>
              {section.items.map((item, itemIndex) => {
                return (
                  <AnimatePresence mode="wait" key={`${section.id}-${item.label}-${itemIndex}`}>
                    {isCollapsed ? (
                      <motion.div
                        key={`${section.id}-${item.label}-${itemIndex}-collapsed`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={transitions.item}
                      >
                        <ContextualSidebarNavItem
                          {...item}
                          isCollapsed
                          isActive={activeNavigationHref === item.to}
                          activeLayoutId={`nav-${section.id}-${itemIndex}`}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key={`${section.id}-${item.label}-${itemIndex}-expanded`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={transitions.item}
                      >
                        <ContextualSidebarNavItem
                          {...item}
                          isCollapsed={false}
                          isActive={activeNavigationHref === item.to}
                          activeLayoutId={`nav-${section.id}-${itemIndex}`}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Workspace Brand indicator chip — org admin/owner only when branding active */}
      {(() => {
        const isAdmin = accountRole === 'organization_owner' || accountRole === 'organization_admin';
        if (!isWorkspaceBrandingActive || !isAdmin || isCollapsed) return null;
        return (
          <div className="px-3 pb-2">
            <Tooltip content="Branding is managed at workspace level." position="right">
              <span className="ax-badge ax-badge--xs ax-badge--neutral select-none">
                <AppIcon name="palette" className="w-3 h-3" />
                Workspace Brand
              </span>
            </Tooltip>
          </div>
        );
      })()}

      {/* User Profile - Operator Identity Panel */}
      <motion.div
        className={cn(
          'border-t border-[color:var(--sidebar-section-border)]',
          isCollapsed ? 'p-2' : 'p-3'
        )}
        layout
        transition={transitions.sidebar}
      >
        <AnimatePresence mode="wait">
          {isCollapsed ? (
            <motion.div
              key="collapsed-profile"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={transitions.item}
            >
              <Tooltip
                content={
                  sidebarIdentity?.capacityLabel
                    ? `${user?.name ?? 'Signed in'} · ${sidebarIdentity.capacityLabel}`
                    : (user?.name ?? 'Signed in')
                }
                position="right"
              >
                <NavLink
                  to={accountSettingsHref}
                  className="flex items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50"
                  aria-label="Open account settings"
                >
                  <Avatar
                    name={user?.name || 'Signed in user'}
                    src={user?.avatar}
                    colorSeed={user?.id || user?.email || user?.name}
                    size={32}
                    ring={false}
                    decorative
                    className={SHELL_NAV.profileRing}
                  />
                </NavLink>
              </Tooltip>
            </motion.div>
          ) : (
            <motion.div
              key="expanded-profile"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={transitions.item}
              className="flex items-center gap-2.5"
            >
              <NavLink
                to={accountSettingsHref}
                className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50"
                aria-label="Open account settings"
              >
                <Avatar
                  name={user?.name || 'Signed in user'}
                  src={user?.avatar}
                  colorSeed={user?.id || user?.email || user?.name}
                  size={32}
                  ring={false}
                  decorative
                  className={SHELL_NAV.profileRing}
                />
                <motion.div
                  className="flex-1 min-w-0"
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ ...transitions.label, delay: 0.04 }}
                >
                  <p className={SHELL_NAV.profileName}>
                    {user?.name}
                  </p>
                  {sidebarIdentity?.capacityLabel ? (
                    <p
                      className={SHELL_NAV.profileCapacity}
                      data-testid="sidebar-operating-capacity"
                    >
                      {sidebarIdentity.capacityLabel}
                    </p>
                  ) : (
                    <div className="mt-0.5">
                      <UserBadge user={user || undefined} size="sm" showIcon={false} variant="glass" />
                    </div>
                  )}
                </motion.div>
              </NavLink>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default SidebarNavigation;
